/**
 * Las suscripciones que forman el MRR del período, cada una con deep link a su
 * **registro exacto** en Stripe: el customer y la suscripción, no la home ni una
 * búsqueda.
 *
 * Cuando falta el id externo no se dibuja un link roto: la fila muestra "sin
 * vincular" y ofrece vincularlo, igual que un KPI sin dato. Una suscripción sin
 * registro en Stripe es plata que el panel dice tener y el sistema de cobro no
 * confirma, así que la fila va en ámbar y respira más que las sanas.
 *
 * La acción de vincular **lleva a Admin**, que es donde se administran las fuentes
 * y los ids externos. No abre un formulario acá porque hoy no hay endpoint que
 * escriba ids externos: un botón que no puede terminar lo que promete es peor que
 * el estado vacío solo.
 */
import {
  SEGMENT_LABELS,
  formatMetricValue,
  formatPeriodo,
  type Links,
  type Provenance,
  type SubscriptionRef,
} from '@ops/contracts'

import {
  DataTable,
  ExternalLink,
  Panel,
  ProvenanceBadge,
  StatusPill,
  type Column,
} from '../../ui'

export interface SuscripcionesStripeProps {
  suscripciones: readonly SubscriptionRef[]
  links: Links
  periodo: string
  /**
   * Fuente y frescura de los importes y los ids de esta tabla. Regla 1: es la
   * tabla más densa de `/revenue` —62 importes— y era la única de las cuatro sin
   * badge. Va a nivel de bloque y no por fila porque `SubscriptionRef` no trae
   * `provenance`: es la misma carga de Stripe del período para todas las filas,
   * la que publica la serie mensual. `null` = sin declarar, y el badge lo dice.
   */
  provenance: Provenance | null
  /** Reloj compartido de la pantalla, para que la frescura sea consistente. */
  now: Date
  onVincular: (clientNombre: string) => void
}

export function SuscripcionesStripe({
  suscripciones,
  links,
  periodo,
  provenance,
  now,
  onVincular,
}: SuscripcionesStripeProps) {
  const linksDe = (clientId: string) => links.clientes[clientId] ?? null
  const sinVincular = suscripciones.filter(
    (s) => linksDe(s.clientId)?.stripeSubscription == null,
  )
  const total = suscripciones.reduce((acc, s) => acc + (s.mrr ?? 0), 0)

  const columnas: Column<SubscriptionRef>[] = [
    {
      key: 'cliente',
      header: 'Cliente',
      rowHeader: true,
      width: '200px',
      sortValue: (s) => s.clientNombre,
      render: (s) => s.clientNombre,
    },
    {
      key: 'segmento',
      header: 'Segmento',
      width: '130px',
      sortValue: (s) => SEGMENT_LABELS[s.segmento],
      render: (s) => (
        <StatusPill
          size="sm"
          icono={false}
          severidad={s.segmento === 'enterprise' ? 'ok' : 'unknown'}
        >
          {SEGMENT_LABELS[s.segmento]}
        </StatusPill>
      ),
    },
    {
      key: 'mrr',
      header: 'MRR',
      align: 'right',
      width: '110px',
      sortValue: (s) => s.mrr,
      render: (s) => formatMetricValue(s.mrr, 'usd'),
    },
    {
      key: 'customer',
      header: 'Customer en Stripe',
      width: '220px',
      filterValue: (s) => s.stripeCustomerId ?? 'sin vincular',
      render: (s) => (
        <ExternalLink
          link={linksDe(s.clientId)?.stripeCustomer ?? null}
          variante="chip"
          sistemaFaltante="Stripe"
          onVincular={() => onVincular(s.clientNombre)}
          textoVincular="Vincular en Admin"
        >
          {s.stripeCustomerId ?? ''}
        </ExternalLink>
      ),
    },
    {
      key: 'subscription',
      header: 'Suscripción en Stripe',
      width: '230px',
      filterValue: (s) => s.stripeSubscriptionId ?? 'sin vincular',
      render: (s) => (
        <ExternalLink
          link={linksDe(s.clientId)?.stripeSubscription ?? null}
          variante="chip"
          sistemaFaltante="Stripe"
          onVincular={() => onVincular(s.clientNombre)}
          textoVincular="Vincular en Admin"
        >
          {s.stripeSubscriptionId ?? ''}
        </ExternalLink>
      ),
    },
  ]

  return (
    <Panel
      titulo={`Suscripciones activas de ${formatPeriodo(periodo)}`}
      subtitulo="Cada fila abre su registro exacto en Stripe, en pestaña nueva."
      padding="none"
      acciones={
        <ProvenanceBadge
          provenance={provenance}
          now={now}
          hrefAdmin="/admin"
        />
      }
      severidad={sinVincular.length > 0 ? 'warning' : undefined}
      footer={
        <span>
          {formatMetricValue(suscripciones.length, 'count')} suscripciones ·{' '}
          <b className="font-semibold text-text tabular">
            {formatMetricValue(total, 'usd')}
          </b>{' '}
          de MRR ·{' '}
          {sinVincular.length === 0 ? (
            'todas vinculadas a Stripe'
          ) : (
            <b className="font-semibold text-warning-text">
              {sinVincular.length === 1
                ? '1 sin suscripción vinculada'
                : `${sinVincular.length} sin suscripción vinculada`}
            </b>
          )}
        </span>
      }
    >
      <DataTable
        columns={columnas}
        rows={suscripciones}
        rowKey={(s) => s.clientId}
        rowSeverity={(s) =>
          linksDe(s.clientId)?.stripeSubscription == null ? 'warning' : 'ok'
        }
        densidadPorSeveridad
        filtroPlaceholder="Buscar cliente o id de Stripe"
        emptyMessage="No hay suscripciones en el período."
        caption={
          <>
            Suscripciones que suman el MRR del período, ordenadas por MRR. Los
            importes y los ids salen de la misma carga de Stripe que la serie
            mensual, con la frescura del badge de arriba. Las filas en ámbar no
            tienen su registro en Stripe: el panel dice tener esa plata y el
            sistema de cobro no lo confirma.
          </>
        }
      />
    </Panel>
  )
}
